'use client';

import { startTransition, useEffect, useState } from 'react';
import { UserStatus } from '@/src/lib/customer/types';
import { useNotify } from '@/src/lib/notification/notify';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Dictionary } from '@/src/lib/dictionary/types';
import { NOTIFICATION_QUERY_PARAM, NotificationMode, NotificationType } from '@/src/app/components/Notification/types';
import { isCustomerVerified } from '@/src/lib/customer/business/customer-verified';
import { eventBus } from '@/src/lib/core/event/bus';
import { EventBusName } from '@/src/lib/core/event/types';
import { hashString } from '@/src/lib/utils/hash-string';
import { AUTH0_ERROR_EMAIL_VERIFICATION_REQUIRED, NOTIFICATION_USER_IS_UNVERIFIED_ID } from '@/src/lib/utils/constants';
import { revalidateProfile } from '@/src/lib/auth/business/revalidate-profile';
import { getAccessToken } from '@auth0/nextjs-auth0';
import { useUser } from '@/src/lib/auth/hooks/use-user';
function AuthNotificatorClient({
  userStatus,
  dict
}: {
  userStatus: UserStatus;
  dict: Dictionary;
}) {
  const {
    user,
    isLoading
  } = useUser();
  const {
    toastNotification,
    flashNotification,
    dismissNotification
  } = useNotify();
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  function removeNotificationFromSearchParams() {
    if (!searchParams) return;
    const searchParamsCopy = new URLSearchParams(searchParams.toString());
    searchParamsCopy.delete(NOTIFICATION_QUERY_PARAM);
    router.replace(`${pathname}${searchParamsCopy.size ? '?' : ''}${searchParamsCopy.toString()}`);
  }
  const [userIsVerified, setUserIsVerified] = useState(false);
  const [pollServerForUserStatus, setPollServerForUserStatus] = useState(false);
  useEffect(() => {
    function onUserRegistered() {
      setPollServerForUserStatus(true);
    }
    eventBus.on(EventBusName.USER_REGISTERED, onUserRegistered);
    return () => {
      eventBus.off(EventBusName.USER_REGISTERED, onUserRegistered);
    };
  }, [pollServerForUserStatus]);
  useEffect(() => {
    if (!pollServerForUserStatus) {
      return;
    }
    if (userIsVerified) {
      revalidateProfile().then(() => {
        router.refresh();
      });
      return () => dismissNotification(NOTIFICATION_USER_IS_UNVERIFIED_ID);
    }
    flashNotification(dict.messages.user.isUnverified.title, NotificationType.INFO, {
      description: dict.messages.user.isUnverified.description,
      isClosable: false,
      id: NOTIFICATION_USER_IS_UNVERIFIED_ID
    });
    const i = setInterval(async () => {
      if (!user) {
        return;
      }
      const isVerified = await isCustomerVerified();
      setUserIsVerified(isVerified);
      if (isVerified) {
        dismissNotification(NOTIFICATION_USER_IS_UNVERIFIED_ID);
      }
    }, 60000);
    return () => clearInterval(i);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userIsVerified, pollServerForUserStatus, user]);
  useEffect(() => {
    if (userStatus === UserStatus.GUEST) return;
    setUserIsVerified(userStatus === UserStatus.VERIFIED);
    getAccessToken().then(() => {
      if (userStatus === UserStatus.REGISTERED) {
        setPollServerForUserStatus(true);
      }
    }).catch(() => {
      if (user && !isLoading) {
        toastNotification(dict.messages.generic.notAuthorized, NotificationType.ERROR);
        startTransition(() => {
          return router.refresh();
        });
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pathname, user, userStatus]);
  useEffect(() => {
    const notificationQuery = searchParams?.get(NOTIFICATION_QUERY_PARAM);
    if (notificationQuery) {
      const parsedErrorQuery = JSON.parse(notificationQuery);
      let message = parsedErrorQuery.message || parsedErrorQuery.errorDescription;
      if (!message) return;
      if (message === AUTH0_ERROR_EMAIL_VERIFICATION_REQUIRED) {
        message = dict.messages.generic.emailNotVerifiedError;
      }
      const mode = parsedErrorQuery.mode || NotificationMode.FLASH;
      const type = parsedErrorQuery.type || NotificationType.ERROR;
      const notificationService = (message: string, type: NotificationType) => {
        if (mode === NotificationMode.TOAST) {
          return toastNotification(message, type);
        }
        return hashString(message).then(id => flashNotification(message, type, {
          id
        }));
      };
      if (message) {
        notificationService(message, type);
        removeNotificationFromSearchParams();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);
  return null;
}
export default AuthNotificatorClient;